import { useMemo, useState } from "react";
import { Box, CircularProgress, Grid } from "@mui/material";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import Details from "../02-Doctors/Details";
import Form from "../02-Doctors/Form";
import { useDoctorsDirectory } from "../utils/useDoctorsDirectory";
import SuccessModal from "../components/SuccessModal";
import { Helmet } from "react-helmet-async";

const DoctorsDetails = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [successOpen, setSuccessOpen] = useState(false);

  const { doctors, loading } = useDoctorsDirectory();

  const doctorId =
    searchParams.get("doctor") || location.state?.doctorId || null;

  const doctor = useMemo(() => {
    if (location.state?.doctor) {
      return location.state.doctor;
    }

    return (
      doctors.find((item) => {
        const id = item?.uid || item?.id || item?.cedula;
        return id === doctorId;
      }) || null
    );
  }, [doctorId, doctors, location.state]);

  const handleSuccess = () => {
    setSuccessOpen(true);
  };

  const handleCloseModal = () => {
    setSuccessOpen(false);
    // Redirigir a servicios
    navigate("/servicios");
  };

  if (loading) {
    return (
      <Box sx={design.Loader}>
        <CircularProgress />
      </Box>
    );
  }

  if (!doctor) {
    return (
      <Box sx={design.Loader}>No se encontró la información del médico.</Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>
          {doctor
            ? `${doctor.nombre} ${doctor.apellido_paterno} | Médico especialista`
            : "Directorio de Doctores | MEDAM"}
        </title>

        <meta
          name="description"
          content={
            doctor
              ? `Agenda una consulta con ${doctor.nombre} ${doctor.apellido_paterno}, especialista en ${doctor.especialidad}.`
              : "Encuentra médicos especialistas en MEDAM y agenda tu consulta."
          }
        />

        <meta
          name="keywords"
          content={
            doctor
              ? `${doctor.nombre}, ${doctor.especialidad}, médico MEDAM, consulta médica`
              : "médicos, especialistas, MEDAM, consulta médica"
          }
        />

        <meta property="og:title" content="Directorio de Doctores MEDAM" />

        <meta
          property="og:description"
          content={
            doctor
              ? `Consulta la información de ${doctor.nombre} ${doctor.apellido_paterno}`
              : "Directorio médico MEDAM"
          }
        />

        <meta
          property="og:image"
          content={
            doctor?.imagen ||
            doctor?.img ||
            "https://centromedicomedam.com/IMG/centromedicoMEDAM/Logo_Centro_Medico_Medam.png"
          }
        />
      </Helmet>
      <Box sx={design.Page}>
        <Grid container sx={design.Card}>
          <Grid size={design.Left}>
            <Details data={doctor} />
          </Grid>

          <Grid size={design.Right}>
            <Form doctor={doctor} onSuccess={handleSuccess} />
          </Grid>
        </Grid>
      </Box>

      {/* Modal de éxito */}
      <SuccessModal open={successOpen} onClose={handleCloseModal} />
    </>
  );
};

export default DoctorsDetails;

const design = {
  Page: {
    background: "#F5F7FB",
    minHeight: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: {
      xs: 2,
      md: 5,
    },
  },

  Card: {
    width: {
      xs: "96%",
      md: "70%",
      lg: "85%",
      xl: "65%",
    },
    backgroundColor: "#FFFFFF",
    borderRadius: "20px",
    overflow: "hidden",
    boxShadow: "0px 20px 60px rgba(0,0,0,.08)",
  },

  Loader: {
    minHeight: "70vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    fontSize: 20,
    fontWeight: 600,
    fontFamily: "Plus Jakarta Sans",
  },

  Left: {
    xs: 12,
    md: 4,
    lg: 4,
    xl: 4,
  },

  Right: {
    xs: 12,
    md: 8,
    lg: 8,
    xl: 8,
  },
};
